<template>
  <div class="steam-selection">
    <div class="selection-card">
      <div class="landing-hero">
        <h1 class="hero-title">
          <span class="hero-icon">🎮</span>
          <span class="hero-brand">steamplayti.me</span>
          <span class="hero-title-sub">{{ $t('landing.heroTagline') }}</span>
        </h1>
        <p class="selection-subtitle">{{ $t('landing.subtitle') }}</p>
        <ul class="features-list">
          <li>{{ $t('landing.feature1') }}</li>
          <li>{{ $t('landing.feature2') }}</li>
          <li>{{ $t('landing.feature3') }}</li>
          <li>{{ $t('landing.feature5') }}</li>
          <li>{{ $t('landing.featureFamily') }}</li>
        </ul>

        <div class="steam-input-form">
          <div class="input-group">
            <input
              type="text"
              :value="steamInput"
              :placeholder="$t('landing.inputPlaceholder')"
              class="steam-input"
              :disabled="resolving"
              @input="$emit('update:steamInput', $event.target.value)"
              @keyup.enter="submit"
            >
            <button
              class="resolve-btn"
              :disabled="!canSubmit"
              @click="submit"
            >
              {{ resolving ? $t('common.loading') : $t('landing.showMyStats') }}
            </button>
          </div>
          <p class="tiny-note">{{ $t('landing.privacyNote') }}</p>
          <p class="tiny-note">{{ $t('landing.stayPublicNote') }}</p>
          <p class="tiny-note warning-note">
            {{ $t('landing.expiryNote') }}
          </p>

          <div v-if="steamError" class="alert alert-danger" role="alert" aria-live="assertive">
            <span class="alert-ico" aria-hidden="true">⚠️</span>
            <span class="alert-text">{{ steamError }}</span>
          </div>
          <div v-if="steamSuccess" class="alert alert-success" role="status" aria-live="polite">
            <span class="alert-ico" aria-hidden="true">✅</span>
            <span class="alert-text">{{ steamSuccess }}</span>
          </div>
        </div>
      </div>

      <div class="landing-insights">
        <div v-if="topGames.length" class="landing-card" role="complementary">
          <h3 class="landing-card-title">{{ $t('landing.topGamesTitle') }}</h3>
          <ol class="landing-top-games">
            <li v-for="game in topGames" :key="game.appId" class="landing-top-game">
              <img
                class="landing-top-game-img"
                :src="gameHeaderUrl(game)"
                :alt="game.name"
                loading="lazy"
                @error="$event.target.style.visibility='hidden'"
              >
              <span class="landing-top-game-name">{{ game.name }}</span>
              <span class="landing-top-game-hours">{{ formatPlaytime(game.totalMinutes) }}</span>
            </li>
          </ol>
          <p v-if="yesterdayUniqueApps" class="landing-card-foot">
            <span class="landing-card-foot-num">{{ yesterdayUniqueApps.toLocaleString() }}</span>
            <span class="landing-card-foot-label">{{ $t('landing.uniqueGamesPlayed') }}</span>
          </p>
        </div>

        <div v-if="topUsers.length" class="landing-card" role="complementary">
          <h3 class="landing-card-title">{{ $t('landing.topPlayersTitle') }}</h3>
          <ol class="landing-top-games">
            <li v-for="user in topUsers" :key="user.steamId" class="landing-top-game">
              <img
                class="landing-top-user-avatar"
                :src="user.avatarUrl"
                :alt="user.displayName"
                loading="lazy"
                @error="$event.target.style.visibility='hidden'"
              >
              <router-link
                class="landing-top-game-name landing-top-user-name"
                :to="{ name: 'Dashboard', params: { steamId: user.steamId }, query: { ref: 'top-players' } }"
              >{{ user.displayName }}</router-link>
              <span class="landing-top-game-hours">{{ formatPlaytime(user.totalMinutes) }}</span>
            </li>
          </ol>
          <p v-if="formattedYesterdayPlaytime" class="landing-card-foot">
            <span class="landing-card-foot-num">{{ formattedYesterdayPlaytime }}</span>
            <span class="landing-card-foot-label">{{ $t('landing.trackedAcrossPlayers') }}</span>
          </p>
        </div>

        <div v-if="topAchievers.length" class="landing-card" role="complementary">
          <h3 class="landing-card-title">{{ $t('landing.topAchieversTitle') }}</h3>
          <ol class="landing-top-games">
            <li v-for="user in topAchievers" :key="user.steamId" class="landing-top-game">
              <img
                class="landing-top-user-avatar"
                :src="user.avatarUrl"
                :alt="user.displayName"
                loading="lazy"
                @error="$event.target.style.visibility='hidden'"
              >
              <router-link
                class="landing-top-game-name landing-top-user-name"
                :to="{ name: 'Dashboard', params: { steamId: user.steamId }, query: { ref: 'top-achievers' } }"
              >{{ user.displayName }}</router-link>
              <span class="landing-top-game-hours">🏆 {{ user.unlockedCount.toLocaleString() }}</span>
            </li>
          </ol>
          <p v-if="yesterdayAchievements !== null" class="landing-card-foot">
            <span class="landing-card-foot-num">🏆 {{ yesterdayAchievements.toLocaleString() }}</span>
            <span class="landing-card-foot-label">{{ $t('landing.achievementsUnlocked') }}</span>
          </p>
        </div>

        <div class="landing-card" role="complementary">
          <h3 class="landing-card-title">{{ $t('landing.exampleProfile') }}</h3>
          <router-link
            class="landing-example-user"
            :to="{ name: 'Dashboard', params: { steamId: 'katates' }, query: { ref: 'example' } }"
          >
            <img
              class="landing-example-avatar"
              src="https://avatars.steamstatic.com/1b0b81f52492e1678bbadbba99273245ee68f8a5_full.jpg"
              alt="katates"
              loading="lazy"
              @error="$event.target.style.visibility='hidden'"
            >
            <span class="landing-example-meta">
              <span class="landing-example-name">katates</span>
              <span class="landing-example-since">{{ $t('landing.exampleSince') }}</span>
            </span>
          </router-link>
          <p class="landing-card-text">{{ $t('landing.exampleText') }}</p>
          <router-link
            class="landing-card-foot landing-card-link"
            :to="{ name: 'Dashboard', params: { steamId: 'katates' } }"
          >
            {{ $t('landing.viewProfile') }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DashboardLanding',
  props: {
    steamInput: {
      type: String,
      default: ''
    },
    resolving: {
      type: Boolean,
      default: false
    },
    steamError: {
      type: String,
      default: ''
    },
    steamSuccess: {
      type: String,
      default: ''
    },
    yesterdayTotalMinutes: {
      type: Number,
      default: null
    },
    yesterdayAchievements: {
      type: Number,
      default: null
    },
    yesterdayUniqueApps: {
      type: Number,
      default: null
    },
    yesterdayTopGames: {
      type: Array,
      default: () => []
    },
    yesterdayTopUsers: {
      type: Array,
      default: () => []
    },
    yesterdayTopAchievers: {
      type: Array,
      default: () => []
    },
    formatPlaytime: {
      type: Function,
      required: true
    }
  },
  emits: ['update:steamInput', 'submit'],
  computed: {
    canSubmit() {
      return this.steamInput.trim().length > 0 && !this.resolving
    },
    topGames() {
      return Array.isArray(this.yesterdayTopGames) ? this.yesterdayTopGames : []
    },
    topUsers() {
      return Array.isArray(this.yesterdayTopUsers) ? this.yesterdayTopUsers : []
    },
    topAchievers() {
      return Array.isArray(this.yesterdayTopAchievers) ? this.yesterdayTopAchievers : []
    },
    isYesterdayLoading() {
      if (this.yesterdayTotalMinutes === null) {
        return false
      }

      const minutes = Number(this.yesterdayTotalMinutes)
      return Number.isFinite(minutes) && minutes <= 0
    },
    formattedYesterdayPlaytime() {
      if (this.yesterdayTotalMinutes === null || this.isYesterdayLoading) {
        return ''
      }
      try {
        return this.formatPlaytime(this.yesterdayTotalMinutes)
      } catch (error) {
        return ''
      }
    }
  },
  methods: {
    submit() {
      if (this.resolving || this.steamInput.trim().length === 0) {
        return
      }
      this.$emit('submit')
    },
    gameHeaderUrl(game) {
      return game.headerImage
        || `https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/${game.appId}/header.jpg`
    }
  }
}
</script>

<style scoped>
.steam-selection {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.selection-card {
  background: var(--panel);
  border: 1px solid var(--panel-border);
  border-radius: 16px;
  padding: 40px;
  text-align: center;
  max-width: 880px;
  width: 100%;
}

.landing-hero {
  max-width: 600px;
  margin: 0 auto;
}

.selection-card h2,
.selection-card .hero-title {
  color: var(--text);
  margin-bottom: 10px;
}

.selection-card .hero-title {
  margin-top: 0;
}

.hero-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 2rem;
  flex-wrap: wrap;
}

.hero-title-sub {
  flex-basis: 100%;
  font-size: 1rem;
  font-weight: 600;
  color: var(--muted);
  letter-spacing: 0.2px;
}

.hero-icon {
  font-size: 1.6rem;
}

.hero-brand {
  font-weight: 800;
  letter-spacing: 0.3px;
}

.selection-subtitle {
  color: var(--muted);
  margin-bottom: 30px;
  font-size: 1.1rem;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0 0 22px 0;
  display: grid;
  gap: 8px;
}

.features-list li::before {
  content: '✔';
  color: var(--success);
  margin-right: 8px;
}

.steam-input-form {
  max-width: 640px;
  margin: 0 auto;
  padding: 0 12px;
  text-align: left;
}

.input-group {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

.steam-input {
  flex: 1;
  padding: 15px;
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  background: var(--panel);
  color: var(--text);
}

.steam-input:focus {
  outline: none;
  border-color: var(--primary);
}

.steam-input:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.resolve-btn {
  background: var(--primary);
  color: #0b0e12;
  border: none;
  padding: 15px 25px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s ease;
  white-space: nowrap;
}

.resolve-btn:hover:not(:disabled) {
  background: var(--primary-hover);
}

.resolve-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.tiny-note {
  margin: 8px 2px 0;
  color: var(--muted);
  font-size: 0.85rem;
}

.warning-note {
  color: #f5c16c;
}

.alert {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.95rem;
}

.alert-danger {
  background: rgba(217, 83, 79, 0.1);
  border: 1px solid rgba(217, 83, 79, 0.4);
  color: var(--text);
}

.alert-success {
  background: rgba(92, 184, 92, 0.1);
  border: 1px solid rgba(92, 184, 92, 0.4);
  color: var(--text);
}

.alert-ico {
  font-size: 1.1rem;
}

.alert-text {
  flex: 1;
}

.landing-insights {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 30px;
  text-align: left;
}

.landing-card {
  background: rgba(27, 40, 56, 0.65);
  border: 1px solid rgba(102, 192, 244, 0.35);
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 160px;
}

.landing-card-title {
  margin: 0;
  font-weight: 600;
  color: var(--text);
  font-size: 1.05rem;
}

.landing-card-text {
  margin: 0;
  color: var(--muted);
  font-size: 0.92rem;
  line-height: 1.45;
}

.landing-card-foot {
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid rgba(102, 192, 244, 0.2);
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-height: 43px;
  box-sizing: border-box;
}

.landing-card-foot-num {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--primary);
  flex-shrink: 0;
}

.landing-card-foot-label {
  color: var(--muted);
  font-size: 0.82rem;
}

.landing-top-games {
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.landing-top-game {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  min-height: 28px;
}

.landing-top-game-img {
  width: 46px;
  height: 22px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
}

.landing-top-game-name {
  flex: 1;
  min-width: 0;
  color: var(--text);
  font-size: 0.88rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.landing-top-game-hours {
  color: var(--primary);
  font-size: 0.82rem;
  font-weight: 600;
  flex-shrink: 0;
}

.landing-top-user-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.landing-top-user-name {
  color: var(--text);
  text-decoration: none;
}

.landing-top-user-name:hover {
  color: var(--primary);
  text-decoration: underline;
}

.landing-example-user {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  margin-top: 2px;
}

.landing-example-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid var(--panel-border);
}

.landing-example-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.landing-example-name {
  color: var(--text);
  font-weight: 600;
  font-size: 0.95rem;
}

.landing-example-user:hover .landing-example-name {
  color: var(--primary);
}

.landing-example-since {
  color: var(--muted);
  font-size: 0.78rem;
  margin-top: 1px;
}

.landing-card-link {
  align-items: center;
  color: var(--primary);
  font-weight: 600;
  text-decoration: none;
}

.landing-card-link::after {
  content: '→';
  transition: transform 0.2s ease;
  font-size: 0.95rem;
}

.landing-card-link:hover {
  color: var(--primary-hover);
}

.landing-card-link:hover::after {
  transform: translateX(3px);
}

@media (max-width: 680px) {
  .landing-insights {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .selection-card {
    padding: 20px;
    border-radius: 12px;
  }

  .hero-title {
    font-size: 1.6rem;
    gap: 8px;
  }

  .hero-title-sub {
    font-size: 0.9rem;
  }

  .hero-icon {
    font-size: 1.2rem;
  }

  .steam-selection,
  .selection-card {
    width: 100%;
  }

  .steam-input-form {
    max-width: 100%;
    padding: 0 12px;
  }

  .input-group {
    flex-direction: column;
  }

  .resolve-btn {
    width: 100%;
    padding: 12px 18px;
  }

  .steam-input {
    padding: 12px;
  }
}
</style>
